import { Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ArrowBack, CheckRounded, InfoOutlined } from '@material-ui/icons';
import { FC } from 'react';
import { ReactComponent as InfoIcon } from '../../../Asset/svg/info.svg';
import { ReactComponent as SmsIcon } from '../../../Asset/svg/sms.svg';

interface IProps {
  title: string;
  email?: string;
  onBack?: () => void;
  onRequestSms?: () => void;
  onNeedHelp?: () => void;
}

const AccountConfirm: FC<IProps> = ({ title, email, onBack, onRequestSms, onNeedHelp }) => {
  const cls = useStyles();

  return (
    <div className={cls.container}>
      <div className={cls.checkCircle}>
        <CheckRounded className={cls.checkIcon} />
      </div>

      <h2 className={cls.title} data-cy="confirmation-account">
        {title}
      </h2>

      <p className={cls.description}>
        Vous venez de recevoir un email sur votre adresse {email && <strong>{email}</strong>} pour
        activer votre compte. Veuillez suivre les instructions.
      </p>

      <div className={cls.infoBox}>
        <div className={cls.infoHeader}>
          <InfoOutlined className={cls.infoIcon} />
          <span className={cls.infoTitle}>Vérifiez vos spams</span>
        </div>
        <p className={cls.infoText}>
          Vous venez de recevoir un email pour activer votre compte. Veuillez suivre les
          instructions.
        </p>
      </div>

      <div className={cls.divider} />

      <p className={cls.noEmailText}>
        Vous n&apos;avez pas reçu l&apos;email pour valider votre compte ?
      </p>

      {onRequestSms && (
        <Button
          variant="outlined"
          className={cls.smsButton}
          startIcon={<SmsIcon style={{ width: 15, height: 15 }} />}
          onClick={onRequestSms}
          fullWidth={true}>
          Recevoir un SMS
        </Button>
      )}

      {onBack && (
        <div className={cls.backLink} onClick={onBack} role="button" tabIndex={0}>
          <ArrowBack className={cls.backIcon} />
          <span>Retour vers l&apos;inscription</span>
        </div>
      )}

      {onNeedHelp && (
        <Button
          variant="outlined"
          className={cls.helpButton}
          startIcon={<InfoIcon />}
          onClick={onNeedHelp}
          fullWidth={true}>
          Besoin d&apos;aide ?
        </Button>
      )}
    </div>
  );
};

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    width: '100%',
    maxWidth: 480,
    margin: '0 auto',
    padding: '0 16px',
  },
  checkCircle: {
    width: 64,
    height: 64,
    borderRadius: '20%',
    backgroundColor: 'rgba(0,10,140,0.04)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  checkIcon: {
    fontSize: 32,
    color: '#000A8C',
  },
  title: {
    fontFamily: 'Poppins',
    fontWeight: 700,
    fontSize: 28,
    color: '#000A8C',
    margin: '0 0 16px 0',
    letterSpacing: 0,
  },
  description: {
    fontFamily: 'Poppins',
    fontSize: 15,
    lineHeight: 1.6,
    color: '#0E1247',
    margin: '0 0 24px 0',
    '& strong': {
      fontWeight: 600,
    },
  },
  infoBox: {
    backgroundColor: 'rgba(0,10,140,0.04)',
    borderRadius: 12,
    padding: '16px 20px',
    textAlign: 'left',
    marginBottom: 24,
  },
  infoHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
  },
  infoIcon: {
    fontSize: 20,
    color: '#000A8C',
  },
  infoTitle: {
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: 14,
    color: '#000A8C',
  },
  infoText: {
    fontFamily: 'Poppins',
    fontWeight: 400,
    fontSize: 13,
    lineHeight: 1.5,
    color: '#0E1247',
    margin: 0,
    paddingLeft: 28,
  },
  divider: {
    width: '100%',
    height: 1,
    backgroundColor: '#E3E8F0',
    marginBottom: 24,
  },
  noEmailText: {
    fontFamily: 'Poppins !important',
    fontSize: 14,
    color: '#0E1247',
    margin: '0 0 16px 0',
  },
  smsButton: {
    fontFamily: 'Poppins !important',
    fontWeight: 500,
    fontSize: 14,
    textTransform: 'none',
    color: '#0E1247',
    borderColor: '#CDCEDA',
    borderRadius: 8,
    padding: '10px 24px',
    marginBottom: 16,
    '&:hover': {
      borderColor: '#000A8C',
      backgroundColor: '#F7F7FB',
    },
    '& span': {
      fontFamily: 'Poppins !important',
    },
  },
  backLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    cursor: 'pointer',
    fontFamily: 'Poppins, sans-serif',
    fontSize: 14,
    fontWeight: 500,
    color: '#0E1247',
    width: '100%',
    marginBottom: 35,
    '&:hover': {
      color: '#000A8C',
    },
    '& span': {
      fontFamily: 'Poppins !important',
    },
  },
  backIcon: {
    fontSize: 18,
  },
  helpButton: {
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 500,
    fontSize: 14,
    textTransform: 'none',
    color: '#000A8C',
    borderColor: '#CDCEDA',
    borderRadius: 8,
    padding: '10px 24px',
    '&:hover': {
      borderColor: '#000A8C',
      backgroundColor: '#F7F7FB',
    },
    '& span': {
      fontFamily: 'Poppins !important',
    },
  },
}));

export default AccountConfirm;
